import { type TypedDocumentNode, gql } from '@apollo/client';
import { SpringValue, useSpring } from '@react-spring/web';

import { usePrevious } from '@sorare/core/src/hooks/usePrevious';

import type { useSimpleCardXpProgressBar_anyCard } from './__generated__/useSimpleCardXpProgressBar.graphql';

type Springs = {
  level: SpringValue<number>;
  percentage: SpringValue<number>;
};
type Args = {
  anyCard: useSimpleCardXpProgressBar_anyCard;
  delay?: number;
};
export const useSimpleCardXpProgressBar = ({
  anyCard,
  delay = 0,
}: Args): Springs => {
  const { grade, xp, xpNeededForCurrentGrade, xpNeededForNextGrade } = anyCard;

  const previousCard = usePrevious(anyCard);

  const config = {
    mass: 1,
    friction: 80,
    tension: 340,
  };

  const hasLeveledUp = previousCard && previousCard?.xp !== xp;

  const progress =
    ((xp - xpNeededForCurrentGrade) /
      ((xpNeededForNextGrade || 0) - xpNeededForCurrentGrade)) *
    100;
  const previousProgress =
    hasLeveledUp && previousCard
      ? (previousCard.xp - previousCard.xpNeededForCurrentGrade) /
        ((previousCard.xpNeededForNextGrade || 0) -
          previousCard.xpNeededForCurrentGrade)
      : progress;

  const previousGrade = previousCard?.grade || grade;

  const [{ percentage, level }] = useSpring(
    () => ({
      from: {
        percentage: previousProgress,
        level: Math.max(0, previousGrade),
      },
      ...(hasLeveledUp && {
        delay,
        to: [
          {
            percentage: 100,
            config,
          },
          {
            immediate: true,
            percentage: 0,
            level: grade,
            config,
          },
          {
            percentage: progress,
            config,
          },
        ],
      }),
    }),
    [anyCard]
  );

  return {
    level,
    percentage,
  };
};

useSimpleCardXpProgressBar.fragments = {
  anyCard: gql`
    fragment useSimpleCardXpProgressBar_anyCard on AnyCardInterface {
      slug
      grade
      xpNeededForCurrentGrade
      xpNeededForNextGrade
      xp
    }
  ` as TypedDocumentNode<useSimpleCardXpProgressBar_anyCard>,
};
